import type { ComponentType, KeyboardEvent, ReactElement, Ref } from 'react';
import { forwardRef, useMemo } from 'react';
import ReactSelect, { components } from 'react-select';
import type {
  Props as ReactSelectProps,
  ContainerProps,
  GroupBase,
  InputProps,
  SelectInstance,
  StylesConfig
} from 'react-select';

import './Select.module.scss';

// A fixed StylesConfig instantiation can't satisfy every generic call —
// per-key prop types like ClearIndicatorProps<Option> use Option
// contravariantly too (e.g. selectOption: (newValue: Option) => void), so
// StylesConfig<unknown, ...> is neither a sub- nor supertype of
// StylesConfig<Option, ...>. Building it fresh per Option/IsMulti/Group
// keeps every callback typed against the caller's actual generics instead
// of casting one fixed instantiation into another.
function createDefaultStyles<
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>
>(): StylesConfig<Option, IsMulti, Group> {
  return {
    valueContainer: (baseStyles, _state) => ({
      ...baseStyles,
      padding: '1px 8px'
    }),
    control: (baseStyles, state) => ({
      ...baseStyles,
      borderColor: state.isFocused ? '#36ad95' : '#ccc',
      borderRadius: '3px',
      borderWidth: '2px',
      boxShadow: 'none',
      minHeight: '39px',
      '&:hover': {
        borderColor: state.isFocused ? '#36ad95' : '#ccc'
      }
    }),
    multiValue: (baseStyles, _state) => ({
      ...baseStyles,
      backgroundColor: 'rgba(26, 188, 156, 0.08)',
      border: 'solid 1px rgba(26, 188, 156, 0.5)',
      color: '#1abc9c'
    }),
    dropdownIndicator: (baseStyles, _state) => ({
      ...baseStyles,
      padding: '7px 8px'
    }),
    clearIndicator: (baseStyles, _state) => ({
      ...baseStyles,
      padding: '7px 8px'
    })
  };
}

export type Props<
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>
> = ReactSelectProps<Option, IsMulti, Group> & {
  customInputComponent?: ComponentType<InputProps<Option, IsMulti, Group>>;
  // react-select acts on Enter only while its menu is open with an option
  // focused; every other Enter reaches the browser, which submits the
  // surrounding form. Pass false on a form whose submit is destructive — a
  // full-replacement save that a stray keystroke must not be able to trigger.
  enterSubmitsForm?: boolean;
};

function SelectInner<
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>
>(
  {
    customInputComponent,
    styles,
    enterSubmitsForm = true,
    components: callerComponents,
    ...restProps
  }: Props<Option, IsMulti, Group>,
  ref: Ref<SelectInstance<Option, IsMulti, Group>>
): ReactElement {
  const defaultStyles = useMemo(
    () => createDefaultStyles<Option, IsMulti, Group>(),
    []
  );

  // Space is a commit key in react-select only while the filter is empty; once
  // anything is typed it is an ordinary character. That split makes a space
  // mean two different things in one control, and on a long option list the
  // first one commits whichever option the menu happens to have focused.
  // Stopping the key short of the container handler leaves the browser to type
  // it, so a space is always a character here.
  //
  // react-select renders this Input only when the select is searchable — a
  // non-searchable one gets a DummyInput instead — so the WAI-ARIA listbox
  // behaviour, where Space selects, is out of reach of this and stays intact.
  const spaceTypingInput = useMemo(() => {
    const Base = customInputComponent ?? components.Input;

    return function SpaceTypingInput(
      inputProps: InputProps<Option, IsMulti, Group>
    ): ReactElement {
      return (
        <Base
          {...inputProps}
          onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
            if (
              event.key === ' ' &&
              inputProps.value === '' &&
              inputProps['aria-expanded']
            ) {
              event.stopPropagation();
            }
          }}
        />
      );
    };
  }, [customInputComponent]);

  const callerSelectContainer = callerComponents?.SelectContainer;

  // react-select puts its keydown handler on the select container's innerProps,
  // so decorating that container composes with it instead of replacing it. The
  // `onKeyDown` prop cannot be used for this: react-select calls it *before* its
  // own switch and skips that switch entirely if the caller prevented default,
  // so swallowing Enter there would also stop Enter from picking an option.
  const submitGuardingContainer = useMemo(() => {
    if (enterSubmitsForm) return undefined;

    const Container = callerSelectContainer ?? components.SelectContainer;

    return function SubmitGuardingContainer(
      containerProps: ContainerProps<Option, IsMulti, Group>
    ): ReactElement {
      const { innerProps } = containerProps;

      return (
        <Container
          {...containerProps}
          innerProps={{
            ...innerProps,
            onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
              innerProps?.onKeyDown?.(event);
              // react-select calls preventDefault on exactly the keys it acts
              // on, so an Enter still live here is one it ignored — and the
              // browser would otherwise submit the form with it.
              if (event.defaultPrevented) return;
              if (event.key === 'Enter') event.preventDefault();
            }
          }}
        />
      );
    };
  }, [enterSubmitsForm, callerSelectContainer]);

  return (
    <ReactSelect
      ref={ref}
      classNamePrefix="react-select"
      styles={styles || defaultStyles}
      // Tab is how a keyboard user leaves a control they opened only to read
      // the choices. react-select commits whichever option the menu happens to
      // have focused on the way out, and on the group and packages editors that
      // value goes straight into a full-replacement save. Enter and Space still
      // commit; a caller that wants the one-keystroke commit passes the prop
      // back, since restProps is spread after this.
      tabSelectsValue={false}
      {...restProps}
      components={{
        Input: spaceTypingInput,
        ...callerComponents,
        ...(submitGuardingContainer && {
          SelectContainer: submitGuardingContainer
        })
      }}
    />
  );
}

type SelectComponent = <
  Option = unknown,
  IsMulti extends boolean = boolean,
  Group extends GroupBase<Option> = GroupBase<Option>
>(
  props: Props<Option, IsMulti, Group> & {
    ref?: Ref<SelectInstance<Option, IsMulti, Group>>;
  }
) => ReactElement;

export const Select = forwardRef(SelectInner) as SelectComponent;
